import React, { useEffect, useRef, useState } from 'react';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const About = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].about;
  const config = useConfig();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const identityBadges = [
    t.badges.cinematic,
    t.badges.sacred,
    t.badges.postHuman,
    t.badges.epic,
    t.badges.biotech,
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Decorative Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="mb-6">
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">{t.subtitle}</span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6 text-glow-divine" style={{ color: 'hsl(42 50% 52%)' }}>
                {t.title}
              </h2>
            </div>

            <div className="space-y-6 text-foreground/80 text-lg leading-relaxed">
              <p>{t.text1}</p>
              <p>{t.text2}</p>
              <p className="text-primary/90 font-medium">{t.text3}</p>
            </div>

            {/* Identity Badges */}
            <div className="mt-8 flex flex-wrap gap-3">
              {identityBadges.map((badge, index) => (
                <Badge
                  key={badge}
                  variant="outline"
                  className="px-4 py-2 text-sm border-primary/30 text-primary hover:bg-primary/10 transition-all"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isVisible ? 'fade-in-up 0.6s ease-out forwards' : 'none',
                  }}
                >
                  {badge}
                </Badge>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-all" />
              <div className="relative aspect-square rounded-2xl overflow-hidden border border-border/50">
                <img
                  src={config.images.aboutImage}
                  alt="Glass altar with golden circuitry and a luminous choir"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;